import React, { useMemo, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { checkmarkOutline, chevronDown } from 'ionicons/icons';
import SectionCard from '@/components/SectionCard';
import {
  ENV,
  PREVIEW_DEGREES,
  PREVIEW_SHEET_BREAKPOINT,
  PREVIEW_SHEET_BREAKPOINTS,
} from '@/modules/config';
import { RecipientChip, type Contact } from '@/modules/contact';
import { INTENT_NOUN, type MessageIntent } from '@/modules/template';
import { newPreview } from '@/modules/topic/preview';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { SMSLinkPreviewCard } from './compose';
import { INTENT_SYMBOL, ShareWizardLogic } from './logic';
import { PreviewLogic } from './preview_logic';
import styles from './TabbedPreviewSheet.module.css';

/**
 * The message as it will be sent, addressed to one of its recipients. When
 * there are multiple recipients, renders the first recipient as a chip that
 * can be clicked to change the preview to a different recipient; a message to
 * one person has no one else to offer, so the recipient is rendered as
 * non-interactive plain text.
 */
const DirectTextPreview: React.FC<{
  intent: MessageIntent;
  recipients: readonly Contact[];
}> = ({ intent, recipients }) => {
  const { getPreviewSegmentsForRecipient } = useShareWizardContext();
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const recipient =
    recipients.find(contact => contact.localId === recipientId) ??
    recipients[0];
  const canPickAnother = recipients.length > 1;

  return (
    <>
      {getPreviewSegmentsForRecipient(intent, recipient).map((segment, i) =>
        segment.perRecipient && canPickAnother ? (
          <RecipientChip
            key={i}
            text={segment.text}
            recipients={recipients}
            onPick={contact => setRecipientId(contact.localId)}
            pickerHeader="Preview the message to"
            ariaLabel={`Previewing for ${segment.text}. Preview for someone else`}
            testId="preview-sheet-recipient-chip"
          />
        ) : (
          <React.Fragment key={i}>{segment.text}</React.Fragment>
        )
      )}
    </>
  );
};

/**
 * The composer's preview (mocks 9d–9f): what this share's messages say, as
 * they will be sent and as they would read some degrees removed.
 *
 * The title is the degree menu — the message as sent, or as the 1st, 2nd, 3rd
 * or 10th person along would reshare it, each of those marked hypothetical
 * because nobody has reshared anything yet. Under it are tabs for the intents
 * possible for the current sharing action. A small badge to the side of each
 * intent name indicates how many messages are planned for that intent. A
 * direct text renders addressed to one real recipient, the first unless the sender picks
 * another from the chip their name renders as; a group text renders as the
 * group gets it. Under the message, the link card the topic's image unfurls
 * as, when it has an image.
 *
 * A read-only detour over the composer: it edits nothing, covers "Queue to
 * Send" so the sender dismisses it before committing, and composes rather than
 * fetches.
 */
const TabbedPreviewSheet: React.FC<{
  isOpen: boolean;
  onDismiss: () => void;
}> = ({ isOpen, onDismiss }) => {
  const {
    topic,
    isUpdateFlow,
    getContactsForIntent,
    addressingModeForIntent,
    getPreviewForIntent,
    getHypotheticalPreviewForIntent,
    cycleHypotheticalPeople,
  } = useShareWizardContext();
  const intents = ShareWizardLogic.flowIntents({ isUpdateFlow });
  const [chosenIntent, setChosenIntent] = useState<MessageIntent | null>(null);
  const [degree, setDegree] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [degreeMenu, setDegreeMenu] = useState<{
    open: boolean;
    event?: Event;
  }>({ open: false });

  // The sender's pick while it is still one of this flow's messages, else the
  // first message anyone is getting.
  const intent =
    chosenIntent && intents.includes(chosenIntent)
      ? chosenIntent
      : (intents.find(i => getContactsForIntent(i).length > 0) ?? intents[0]);
  const recipients = getContactsForIntent(intent);
  const linkPreview = useMemo(
    () =>
      topic?.imageData?.trim()
        ? newPreview(
            topic,
            window.location.origin,
            ENV.apiBaseUrl || window.location.origin
          )
        : null,
    [topic]
  );

  // Every look opens on the message as sent. New made-up people are picked on
  // the way out rather than the way in, so the names stay put while the sender
  // reads and the next look brings new ones; onDidDismiss is reached by Done and a
  // drag-down alike.
  const handleDidDismiss = () => {
    cycleHypotheticalPeople();
    setDegree(0);
    onDismiss();
  };

  const chooseDegree = (next: number) => {
    setDegree(next);
    setDegreeMenu({ open: false });
  };

  const preview =
    degree > 0 ? (
      getHypotheticalPreviewForIntent(intent, degree)
    ) : addressingModeForIntent(intent) === 'dm' && recipients.length > 0 ? (
      <DirectTextPreview intent={intent} recipients={recipients} />
    ) : (
      getPreviewForIntent(intent)
    );

  return (
    <IonModal
      isOpen={isOpen}
      initialBreakpoint={PREVIEW_SHEET_BREAKPOINT}
      breakpoints={PREVIEW_SHEET_BREAKPOINTS}
      // Scroll the message at two thirds rather than making the sender drag the
      // sheet to full height first; the composer behind it is the reason the
      // sheet stops short of the top.
      expandToScroll={false}
      handle
      onDidDismiss={handleDidDismiss}
      data-testid="preview-sheet"
    >
      <IonHeader
        className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}
      >
        <IonToolbar className={styles.toolbar}>
          <IonTitle>
            <button
              type="button"
              className={styles.degreeTitle}
              aria-haspopup="menu"
              onClick={e => setDegreeMenu({ open: true, event: e.nativeEvent })}
              data-testid="preview-sheet-degree"
            >
              {PreviewLogic.degreeLabel(degree)}
              <IonIcon icon={chevronDown} aria-hidden="true" />
            </button>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} data-testid="preview-sheet-close">
              Done
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar className={styles.toolbar}>
          <IonSegment
            value={intent}
            onIonChange={e => {
              const next = intents.find(i => i === e.detail.value);
              if (next) setChosenIntent(next);
            }}
          >
            {intents.map(i => (
              <IonSegmentButton
                key={i}
                value={i}
                data-testid={`preview-sheet-intent-${i}`}
              >
                <IonLabel>
                  <span className={styles.intentName}>
                    {INTENT_SYMBOL[i]} {INTENT_NOUN[i].one}
                    <span
                      className={styles.countBadge}
                      data-testid={`preview-sheet-intent-${i}-count`}
                    >
                      {getContactsForIntent(i).length}
                    </span>
                  </span>
                </IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent
        className={`ion-padding ${styles.content}`}
        scrollEvents
        onIonScroll={e => setIsScrolled(e.detail.scrollTop > 0)}
      >
        <SectionCard className="section-card--message-preview" noBottomMargin>
          <div
            className={`section-card__message-preview ${styles.preview}`}
            data-testid="preview-sheet-content"
          >
            {preview}
          </div>
        </SectionCard>
        {linkPreview && (
          <SectionCard
            className={`section-card--sms-link-preview ${styles.linkPreview}`}
            noBottomMargin
          >
            <SMSLinkPreviewCard preview={linkPreview} />
          </SectionCard>
        )}
      </IonContent>
      <IonPopover
        isOpen={degreeMenu.open}
        event={degreeMenu.event}
        onDidDismiss={() => setDegreeMenu({ open: false })}
        data-testid="preview-sheet-degree-menu"
      >
        <IonList lines="none">
          {PREVIEW_DEGREES.map(d => (
            <IonItem
              key={d}
              button
              detail={false}
              onClick={() => chooseDegree(d)}
              data-testid={`preview-sheet-degree-${d}`}
            >
              <IonLabel>
                {PreviewLogic.degreeLabel(d)}
                {d > 0 && (
                  <span className={styles.hypothetical}> (hypothetical)</span>
                )}
              </IonLabel>
              {d === degree && (
                <IonIcon
                  slot="end"
                  icon={checkmarkOutline}
                  aria-hidden="true"
                />
              )}
            </IonItem>
          ))}
        </IonList>
      </IonPopover>
    </IonModal>
  );
};

export default TabbedPreviewSheet;
